import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Download, Loader2, Upload, X, XCircle } from 'lucide-react';
import {
  annoncerErreur, annoncerSucces, BarreProgression, confirmer, telecharger, useTeleversement, ZoneDepot,
} from '../etats';

/* ═══════════════════════════════════════════════════════════════════════
   LE RÉFÉRENTIEL EN CLASSEUR — télécharger la base, téléverser une version

   Télécharger : la base telle qu'elle est, une ligne par élément (les
   actions correctives regroupées dans une cellule du fichier source sont
   séparées), chaque livrable relié à l'action qu'il atteste. Téléverser :
   le serveur ANALYSE d'abord le fichier — feuilles, colonnes, références,
   caractères parasites, questions reconnues — sans rien écrire ; s'il est
   valide, « Appliquer » l'écrit d'un bloc, tout ou rien.
   (`referentiel/classeur.py`)
   ═══════════════════════════════════════════════════════════════════════ */

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const TAILLE_MAX = 10 * 1024 * 1024;

function Rapport({ rapport }) {
  const c = rapport.comparaison;
  return (
    <div className="space-y-4">
      <div className={`encart ${rapport.valide ? '' : 'encart-alerte'}`}>
        {rapport.applique
          ? <><CheckCircle2 className="w-4 h-4" /><p>Le référentiel a été mis à jour.</p></>
          : rapport.valide
            ? <><CheckCircle2 className="w-4 h-4" /><p>Le classeur est valide. Rien n’est encore écrit : « Appliquer » le met en base.</p></>
            : <><XCircle className="w-4 h-4" /><p>Le classeur contient des erreurs : rien ne sera écrit tant qu’elles restent.</p></>}
      </div>
      {rapport.bilan && (
        <p className="text-sm text-[var(--text-main)] leading-relaxed">
          {rapport.bilan.questions_mises_a_jour} question(s) mise(s) à jour, {rapport.bilan.questions_creees} créée(s) ;{' '}
          {rapport.bilan.forces_faiblesses} force(s) et faiblesse(s), {rapport.bilan.actions} action(s),{' '}
          {rapport.bilan.livrables} livrable(s).
          {rapport.bilan.sous_domaines_sans_question_supprimes?.length > 0 && (
            <> Sous-domaine(s) sans question retiré(s) : {rapport.bilan.sous_domaines_sans_question_supprimes.join(', ')}.</>
          )}
          {rapport.bilan.questions_absentes?.length > 0 && (
            <> Question(s) absente(s) du classeur, gardée(s) : {rapport.bilan.questions_absentes.join(', ')}.</>
          )}
        </p>
      )}
      {rapport.erreurs?.length > 0 && (
        <ul className="list-disc pl-5 text-sm text-[var(--alerte)] space-y-1 max-h-56 overflow-y-auto">
          {rapport.erreurs.map((e) => <li key={e}>{e}</li>)}
          {rapport.erreurs_total > rapport.erreurs.length && <li>… et {rapport.erreurs_total - rapport.erreurs.length} autre(s).</li>}
        </ul>
      )}
      {rapport.feuilles?.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[12px] text-[var(--text-muted)]">
              <th className="py-1.5 font-semibold">Feuille</th>
              <th className="py-1.5 font-semibold text-right">Lignes</th>
              <th className="py-1.5 font-semibold text-right">Incomplètes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border)]">
            {rapport.feuilles.map((f) => (
              <tr key={f.nom}>
                <td className="py-1.5 font-semibold text-[var(--text-bold)]">{f.nom}</td>
                <td className="py-1.5 text-right tabular-nums">{f.lignes}</td>
                <td className={`py-1.5 text-right tabular-nums ${f.incompletes ? 'text-[var(--alerte)] font-semibold' : ''}`}>{f.incompletes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {c && (
        <p className="text-sm text-[var(--text-main)] leading-relaxed">
          <span className="font-semibold text-[var(--text-bold)]">Questions :</span>{' '}
          {c.questions_reconnues} reconnue{c.questions_reconnues > 1 ? 's' : ''},{' '}
          {c.questions_nouvelles} nouvelle{c.questions_nouvelles > 1 ? 's' : ''},{' '}
          {c.questions_absentes_du_fichier} de la base absente{c.questions_absentes_du_fichier > 1 ? 's' : ''} du fichier
          {c.id_inconnus?.length > 0 && <>, ID inconnus : {c.id_inconnus.join(', ')}</>}.
        </p>
      )}
      {rapport.anomalies_total > 0 && (
        <div>
          <p className="text-sm font-semibold text-[var(--text-bold)] mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-[var(--attention)]" />
            {rapport.anomalies_total} point{rapport.anomalies_total > 1 ? 's' : ''} à corriger
            {rapport.anomalies_total > rapport.anomalies.length && ` (les ${rapport.anomalies.length} premiers)`}
          </p>
          <ul className="max-h-56 overflow-y-auto divide-y divide-[var(--border)] border border-[var(--border)] rounded-pastille text-[13px]">
            {rapport.anomalies.map((a, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={i} className="px-3 py-2 flex gap-2">
                <span className="text-[var(--text-muted)] shrink-0 tabular-nums">{a.feuille} · l. {a.ligne}</span>
                <span className="text-[var(--text-main)]">{a.colonne ? `${a.colonne} : ` : ''}{a.probleme}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Televersement({ onClose, onApplique }) {
  const [fichier, setFichier] = useState(null);
  const envoi = useTeleversement();
  const [rapport, setRapport] = useState(null);
  const occupe = envoi.statut === 'envoi' || envoi.statut === 'traitement';

  const envoyer = async (chemin) => {
    const donnees = new FormData();
    donnees.append('fichier', fichier);
    try {
      return await envoi.lancer({ url: `${API}/referentiel/classeur/${chemin}/`, donnees });
    } catch (e) {
      // Un classeur refusé revient avec son rapport : on l'affiche.
      if (e.details?.feuilles) return e.details;
      if (!e.annule) annoncerErreur(e.message);
      return null;
    }
  };
  const analyser = async () => setRapport(await envoyer('analyse'));
  const appliquer = async () => {
    if (!await confirmer('Appliquer ce classeur ? Les forces, faiblesses, actions et livrables des sous-domaines '
      + 'du classeur seront remplacés. Les questions et leurs réponses sont mises à jour, jamais supprimées.')) return;
    const r = await envoyer('appliquer');
    setRapport(r);
    if (r?.applique) {
      annoncerSucces('Référentiel mis à jour.');
      onApplique?.();
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" role="dialog" aria-modal="true"
         aria-label="Téléverser une nouvelle version du référentiel"
         onMouseDown={(e) => { if (e.target === e.currentTarget && !occupe) onClose(); }}>
      <div className="bg-[var(--bg-card)] rounded-cadre w-full max-w-2xl shadow-carte flex flex-col max-h-[90vh]">
        <div className="flex items-start justify-between gap-3 p-6 pb-3">
          <div>
            <h3 className="text-xl font-black text-[var(--text-bold)]">Nouvelle version du référentiel</h3>
            <p className="text-xs text-[var(--text-muted)] mt-1 font-semibold">
              Le classeur est d’abord analysé : forme, références, caractères parasites, questions reconnues.
              Rien n’est écrit avant « Appliquer », et tout ou rien.
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={occupe} className="act act-icone act-tertiaire" aria-label="Fermer">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-4 space-y-4">
          <ZoneDepot extensions={['.xlsx']} tailleMax={TAILLE_MAX} fichier={fichier} desactive={occupe}
                     onFichier={(f) => { setFichier(f); setRapport(null); }}
                     onRetirer={() => { setFichier(null); setRapport(null); }}
                     texte="Glissez le classeur ici ou cliquez pour le choisir" />
          {occupe && <BarreProgression valeur={envoi.statut === 'traitement' ? null : envoi.progression} />}
          {rapport && <Rapport rapport={rapport} />}
        </div>
        <div className="p-6 pt-3 flex flex-col sm:flex-row gap-3 border-t border-[var(--border)]">
          <button type="button" onClick={onClose} disabled={occupe} className="act act-contour flex-1">Fermer</button>
          {rapport?.valide && !rapport.applique ? (
            <button type="button" onClick={appliquer} disabled={occupe} aria-busy={occupe || undefined} className="act act-fort flex-1">
              {occupe ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {envoi.statut === 'traitement' ? 'Application…' : 'Appliquer au référentiel'}
            </button>
          ) : (
            <button type="button" onClick={analyser} disabled={!fichier || occupe || rapport?.applique} aria-busy={occupe || undefined} className="act flex-1">
              {occupe ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {envoi.statut === 'traitement' ? 'Analyse…' : 'Analyser le classeur'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function ClasseurReferentiel({ peutTeleverser, onApplique }) {
  const [telechargement, setTelechargement] = useState(false);
  const [ouvert, setOuvert] = useState(false);

  const telechargerBase = async () => {
    setTelechargement(true);
    try {
      const date = new Date().toISOString().slice(0, 10);
      await telecharger(`${API}/referentiel/classeur/`, `referentiel-asso-pro-${date}.xlsx`);
      annoncerSucces('Référentiel téléchargé.');
    } catch (e) {
      annoncerErreur(e.message || 'Le référentiel n’a pas pu être généré.');
    } finally {
      setTelechargement(false);
    }
  };

  return (
    <>
      <button type="button" onClick={telechargerBase} disabled={telechargement} aria-busy={telechargement || undefined}
              className="act act-contour">
        {telechargement ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        Télécharger le référentiel
      </button>
      {peutTeleverser && (
        <button type="button" onClick={() => setOuvert(true)} className="act act-contour">
          <Upload className="w-4 h-4" /> Téléverser une version
        </button>
      )}
      {ouvert && <Televersement onClose={() => setOuvert(false)} onApplique={onApplique} />}
    </>
  );
}
